import { useMemo } from "react";
import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { ChevronDown, Link2 } from "lucide-react";
import type { BootstrapPayload, FormRelatedChip, MenuEntry, RecordData, RelatedCountResult } from "../core";
import { buildWorkspacePath, pushList, pushRecord } from "../core";
import { appIcon } from "./icons";
import { colorForModel } from "./colorForModel";
import { useBootstrap } from "../react/queries/useBoot";
import { useRelatedCounts } from "../react/queries/useRelatedCounts";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";

// `Related ▾` toolbar dropdown (Enhancement 06). Renders as the leftmost
// dropdown in the form toolbar — before Print / Actions. Each chip points at
// a bespoke ir.action; click pushes either a ListDrillFrame (count > 1) or a
// RecordFrame (count === 1 — short-circuit). Chips with count === 0 are
// hidden by default (per-chip `hide-empty="0"` overrides); when every chip
// is hidden, the entire trigger is hidden (cascade).

interface RelatedDropdownProps {
    items: FormRelatedChip[];
    activeId: string | undefined;
    // The parent record's model_key — used so `related_counts` can resolve
    // `$active_record.<field>` tokens in action-domain templates (M2M chips
    // without an inverse field on the target).
    activeModelKey?: string;
    // The parent record's data — used to detect per-chip arity (M2O vs
    // O2M/M2M) from the field value's shape, and to render the linked
    // record's display_name in the M2O case (no counter needed).
    record?: RecordData | null;
    // The parent record's frame index in the current nav stack — used to
    // compute the push target. When undefined (e.g. the record isn't open
    // yet) the trigger renders disabled.
    enabled: boolean;
}

interface RelatedRef {
    id?: string;
    display_name?: string;
}

function resolveSingleRef(value: unknown): RelatedRef | null {
    if (!value || typeof value !== "object" || Array.isArray(value)) return null;
    const obj = value as Record<string, unknown>;
    if (typeof obj.display_name !== "string" && typeof obj.id !== "string") return null;
    return obj as RelatedRef;
}

export function RelatedDropdown({ items, activeId, activeModelKey, record, enabled }: RelatedDropdownProps): ReactNode {
    const boot = useBootstrap();
    const { stack, orgCode, navigateTo } = useWorkspaceNavigation();

    // Only chips bound to an action (Mode B) need a server count round-trip.
    // Action-less chips (Mode A) compute everything client-side from the
    // parent record's value + the parser-derived targetModelKey.
    const actionKeys = useMemo(
        () => items.map((it) => it.action).filter((k): k is string => Boolean(k)),
        [items],
    );
    const countsQ = useRelatedCounts(actionKeys, activeId, enabled, activeModelKey);

    // Map count results back to chips. Missing entries → count 0 (gracefully
    // hidden by the hide-empty default).
    const countsByKey = useMemo(() => {
        const map = new Map<string, RelatedCountResult>();
        for (const r of countsQ.data ?? []) map.set(r.actionKey, r);
        return map;
    }, [countsQ.data]);

    const renderable = useMemo(() => {
        return items
            .map((chip) => {
                const result = chip.action ? countsByKey.get(chip.action) : undefined;
                // Arity detection — `{id, display_name}` shape on the parent
                // record means the chip's source field is a M2O. Anything
                // else (array, missing, scalar) → multi.
                const singleRef = record ? resolveSingleRef(record[chip.name]) : null;
                // Mode A multi (no action) — derive count from the parent's
                // array value; backend round-trip is skipped.
                const parentValue = record ? record[chip.name] : null;
                const arrayCount = Array.isArray(parentValue) ? parentValue.length : 0;
                const count = result?.count ?? (singleRef ? 1 : arrayCount);
                const loneUuid = result?.loneUuid ?? null;
                // modelKey precedence: Mode B `result.modelKey` (action-bound)
                // → Mode A `chip.targetModelKey` (parser-derived). Either is
                // enough to build a drill URL.
                const modelKey = result?.modelKey || chip.targetModelKey || null;
                const path = result?.path ?? null;
                return { chip, count, loneUuid, modelKey, path, singleRef };
            })
            .filter(({ chip, count, singleRef }) =>
                singleRef !== null || count > 0 || !chip.hideEmpty,
            );
    }, [items, countsByKey, record]);

    // Partition into singles (M2O, render first) vs multis (O2M/M2M, render
    // after a separator). Order preserved within each group.
    const { singles, multis } = useMemo(() => {
        const s: typeof renderable = [];
        const m: typeof renderable = [];
        for (const r of renderable) (r.singleRef ? s : m).push(r);
        return { singles: s, multis: m };
    }, [renderable]);

    // Cascade-hide: if every chip is filtered out, the trigger doesn't render.
    if (!enabled || renderable.length === 0) return null;

    const onPick = ({ chip, count, loneUuid, modelKey, path, singleRef }: typeof renderable[number]) => {
        // M2O chip (single-ref) → drill straight to the linked record using
        // the value already on the parent. No round-trip; no list step.
        // `modelKey` comes from the related_counts response.
        if (singleRef?.id && modelKey) {
            navigateTo(buildWorkspacePath(orgCode, pushRecord(stack, {
                kind: "record",
                modelKey,
                recordRef: singleRef.id,
                viewMode: "form",
            })));
            return;
        }
        // Multi-mode: count === 1 short-circuits to the lone record (saved a
        // second round-trip via the `lone_uuid` the count endpoint surfaces).
        const isShortCircuit = count === 1 && loneUuid && modelKey;
        if (isShortCircuit) {
            navigateTo(buildWorkspacePath(orgCode, pushRecord(stack, {
                kind: "record",
                modelKey: modelKey as string,
                recordRef: loneUuid as string,
                viewMode: "form",
            })));
            return;
        }
        // List drill — use the action's URL-facing `path` slug (from the
        // count response), NOT the dotted xml_id, so the URL stays readable.
        // Fall back to the xml_id only when the count query hasn't surfaced a
        // path yet (e.g. still loading or unresolved action).
        navigateTo(buildWorkspacePath(orgCode, pushList(stack, {
            kind: "list",
            actionSlug: path || chip.action || "",
            viewMode: "list",
        })));
    };

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="btn form-toolbar__actions form-toolbar__related"
                    aria-label="Related records"
                    title="Related records"
                >
                    <Link2 size={13} />
                    <span>Related</span>
                    <ChevronDown size={11} className="form-toolbar__actions-chev" />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="rdx-menu related-menu"
                    align="end"
                    sideOffset={6}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                >
                    {singles.map((row) => (
                        <RelatedSingleItem
                            key={row.chip.action ?? row.chip.name}
                            chip={row.chip}
                            singleRef={row.singleRef as RelatedRef}
                            boot={boot.data}
                            onSelect={() => onPick(row)}
                        />
                    ))}
                    {singles.length > 0 && multis.length > 0 ? (
                        <DropdownMenu.Separator className="rdx-menu__separator" />
                    ) : null}
                    {multis.map((row) => (
                        <RelatedMultiItem
                            key={row.chip.action ?? row.chip.name}
                            chip={row.chip}
                            count={row.count}
                            modelKey={row.modelKey}
                            boot={boot.data}
                            onSelect={() => onPick(row)}
                        />
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

// Resolve the chip's textual label (used as M2O subtitle / multi title).
// Priority: explicit DSL `string=` → server-derived fieldLabel from the field
// spec → menu-entry name → action path → field name (absolute last resort).
function chipLabel(chip: FormRelatedChip, boot: BootstrapPayload | undefined): string {
    const menuName = boot ? findMenuNameForAction(boot.menus, chip.action) : null;
    return chip.string || chip.fieldLabel || menuName || chip.action || chip.name;
}

// M2O row — marker icon · two-row text: TITLE = linked record's display_name,
// SUBTITLE = the field's label (so the user knows "LEAD-2026-000024 is the
// Converted Lead", not just a floating ID). No counter. Marker uses the
// same filled-palette treatment as multi rows — colour hashed from the
// chip's targetModelKey so the same target always reads in the same shade
// across forms (e.g. every "Lead" chip wears the same colour).
function RelatedSingleItem({
    chip,
    singleRef,
    boot,
    onSelect,
}: {
    chip: FormRelatedChip;
    singleRef: RelatedRef;
    boot: BootstrapPayload | undefined;
    onSelect: () => void;
}): ReactNode {
    // Icon resolution chain: explicit chip.icon → menu-entry icon → default
    // "bookmark" glyph (this record is bookmarked / referenced from here).
    const menuIcon = boot ? findMenuIconForAction(boot.menus, chip.action) : null;
    const iconName = chip.icon ?? menuIcon ?? "bookmark";
    const Icon = appIcon(iconName);
    const subtitle = chipLabel(chip, boot);
    const title = singleRef.display_name ?? singleRef.id ?? "";
    const colorIdx = colorForModel(chip.targetModelKey ?? "");
    return (
        <DropdownMenu.Item
            className="rdx-menu__item related-menu__item related-menu__item--single"
            onSelect={(e) => {
                e.preventDefault();
                onSelect();
            }}
        >
            <span
                className="related-menu__marker related-menu__marker--tinted"
                data-color={colorIdx}
            >
                <Icon size={14} />
            </span>
            <span className="related-menu__text">
                <span className="related-menu__title">{title}</span>
                {subtitle ? <span className="related-menu__subtitle">{subtitle}</span> : null}
            </span>
        </DropdownMenu.Item>
    );
}

// O2M / M2M row — mirrors the M2O layout (icon · two-row text). The marker
// is a filled square in the action-model's palette colour (same hash that
// the count-badge used to use) with a white icon glyph. TITLE is the bare
// bold count; SUBTITLE is the field label.
function RelatedMultiItem({
    chip,
    count,
    modelKey,
    boot,
    onSelect,
}: {
    chip: FormRelatedChip;
    count: number;
    modelKey: string | null;
    boot: BootstrapPayload | undefined;
    onSelect: () => void;
}): ReactNode {
    // Icon resolution chain: explicit chip.icon → menu-entry icon → default
    // "git-branch" glyph (one-to-many split — visually says "this record
    // has many children").
    const menuIcon = boot ? findMenuIconForAction(boot.menus, chip.action) : null;
    const iconName = chip.icon ?? menuIcon ?? "git-branch";
    const Icon = appIcon(iconName);
    const subtitle = chipLabel(chip, boot);
    const colorIdx = colorForModel(modelKey ?? "");
    return (
        <DropdownMenu.Item
            className="rdx-menu__item related-menu__item related-menu__item--multi"
            onSelect={(e) => {
                e.preventDefault();
                onSelect();
            }}
        >
            <span
                className="related-menu__marker related-menu__marker--filled"
                data-color={colorIdx}
            >
                <Icon size={14} />
            </span>
            <span className="related-menu__text">
                <span className="related-menu__title">{count}</span>
                {subtitle ? <span className="related-menu__subtitle">{subtitle}</span> : null}
            </span>
        </DropdownMenu.Item>
    );
}

// Walk boot.menus to find the menu entry whose action.key matches; return
// its icon. Used as a default icon when the chip didn't declare one.
function findMenuIconForAction(
    menus: Record<string, MenuEntry[]>,
    actionKey: string | null | undefined,
): string | null {
    if (!actionKey) return null;
    for (const entries of Object.values(menus)) {
        const found = walk(entries, actionKey, "icon");
        if (found) return found;
    }
    return null;
}

function findMenuNameForAction(
    menus: Record<string, MenuEntry[]>,
    actionKey: string | null | undefined,
): string | null {
    if (!actionKey) return null;
    for (const entries of Object.values(menus)) {
        const found = walk(entries, actionKey, "name");
        if (found) return found;
    }
    return null;
}


function walk(
    entries: MenuEntry[],
    actionKey: string,
    pick: "icon" | "name" | "modelKey",
): string | null {
    for (const entry of entries) {
        if (entry.action?.key === actionKey) {
            if (pick === "icon") return entry.icon ?? null;
            if (pick === "name") return entry.name ?? entry.action.name ?? null;
            if (pick === "modelKey") return entry.action.modelKey ?? null;
        }
        if (entry.children && entry.children.length > 0) {
            const found = walk(entry.children, actionKey, pick);
            if (found) return found;
        }
    }
    return null;
}
